import {
  SpaceBetween,
  tk,
  FormFieldLabel,
  FormFieldControl,
  FormField,
  TextField,
  Typography,
  Button,
  FormFieldError,
} from '@sunwebgroup/ui-components';

import ServiceBox from '@/scopes/checkout/components/services/ServiceBox';

import { useIntl } from '@/intl';

import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useSetParticipantsBadge } from '@/scopes/checkout/domain/hooks';
import { sub } from 'date-fns';
import { styled } from 'styled-components';
import { isEmpty } from 'ramda';
import { createAncillariesAvailableEvent } from '../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import * as React from 'react';
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon';

const validateAgeBetweenDates = (value: string, min: Date, max: Date) => {
  const date = new Date(value);
  return date >= min && date <= max;
};

const defaultMinBirthdate = sub(new Date(), { years: 18 });
const defaultMaxBirthdate = sub(new Date(), { days: 1 });
const defaultBirthdate = '1990-01-01';
interface FormValues {
  participantBadge: {
    id: string;
    name: string;
    birthdate?: string;
  }[];
}

interface FieldErrors {
  participantBadge?: Record<string, { name?: { message: string }; birthdate?: { message: string } }>;
}

const ParticipantFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk('spacing_sm')};
  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;
const ParticipantFormField = styled(FormField)`
  flex: 1 1 0;
  width: 100%;
`;

const ParticipantFormFieldDate = styled(FormField)`
  flex: 1 1 0;
  width: 100%;
  @media ${tk('mq_desktop_sm')} {
    max-width: 300px;
  }
`;
const SubmitButton = styled(Button)`
  width: max-content;
  align-self: flex-end;
`;
const RoomName = styled(Typography)`
  color: ${tk('color_semantic_text')};
`;

export function ParticipantsBadgeForm() {
  const intl = useIntl();
  const { participants, rooms, setParticipantsBadge } = useSetParticipantsBadge();
  const { sendEvent } = useAnalytics();
  const hasBeenSent = React.useRef(false);

  React.useEffect(() => {
    if (hasBeenSent.current) return;
    sendEvent(
      createAncillariesAvailableEvent({
        result: 'participantsBadge',
      })
    );
    hasBeenSent.current = true;
  }, [sendEvent, hasBeenSent]);

  const form = useForm<FormValues>({
    defaultValues: {
      participantBadge: Object.values(participants ?? {})?.map(({ id, name, birthdate }) => {
        return {
          id,
          name,
          birthdate: birthdate ?? defaultBirthdate,
        };
      }),
    },
    mode: 'onBlur',
  });

  const onSubmit = (result: FormValues) => {
    setParticipantsBadge(result.participantBadge);
    sendEvent({
      event: 'Participants Badge',
      validated: true,
      description: 'Save participants badge form',
      element: 'button',
      result: 'save',
      interaction: true,
    });
  };

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_description' })}
      glyph={getIconForService('participantsBadge')}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <SpaceBetween direction="y" spacing={tk('spacing_md')}>
            {Object.values(rooms ?? {}).map(({ id: roomId, name: roomName, participantIds }) => (
              <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
                <RoomName as="p" $variant="body-md-bold">
                  {roomName}
                </RoomName>
                {participantIds.map((participantId) => (
                  <SpaceBetween direction="y" key={participantId} spacing={tk('spacing_xs')}>
                    <ParticipantRow participantId={participantId} />
                  </SpaceBetween>
                ))}
              </SpaceBetween>
            ))}
            {isEmpty(rooms) && (
              <>
                {Object.values(participants ?? {})?.map((participant) => (
                  <SpaceBetween direction="y" key={participant.id} spacing={tk('spacing_xs')}>
                    <ParticipantRow participantId={participant.id} />
                  </SpaceBetween>
                ))}
              </>
            )}
            <SubmitButton $hierarchy="secondary" type="submit">
              {intl.formatMessage({ id: 'checkout_steps_participants_badge_form_submit_button' })}
            </SubmitButton>
          </SpaceBetween>
        </form>
      </FormProvider>
    </ServiceBox>
  );
}

const ParticipantRow = ({ participantId }: { participantId: string }) => {
  const intl = useIntl();
  const { participants, ageRestrictions } = useSetParticipantsBadge();
  const currentParticipant = participants ? participants[participantId] : undefined;
  const participantIndex = Object.keys(participants ?? {}).findIndex((id) => id === participantId);
  const currentAgeRestriction =
    ageRestrictions && currentParticipant ? ageRestrictions[currentParticipant.ageCategory] : undefined;

  const minBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.min) : defaultMinBirthdate;
  const maxBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.max) : defaultMaxBirthdate;
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  if (!currentParticipant) return null;

  const errorsTyped = errors as FieldErrors;

  return (
    <ParticipantFieldsRow>
      <input {...register(`participantBadge.${participantIndex}.id`, { value: participantId })} type="hidden" />
      <ParticipantFormField
        $error={
          errorsTyped?.participantBadge?.[participantIndex]?.name
            ? Boolean(errorsTyped?.participantBadge?.[participantIndex]?.name)
            : undefined
        }
      >
        <FormFieldLabel>{currentParticipant.alternativeName}</FormFieldLabel>
        <FormFieldControl>
          <Controller
            name={`participantBadge.${participantIndex}.name`}
            control={control}
            rules={{
              pattern: {
                value: /^(?: {0,}[A-Za-zÆæØøÅå'ÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú-]{2,} {0,}){1,}$/,
                message: intl.formatMessage({ id: 'checkout_steps_participants_badge_form_pattern_field' }),
              },
            }}
            render={({ field }) => (
              <TextField.Root>
                <TextField.Content>
                  <TextField.Input
                    {...field}
                    type="text"
                    placeholder={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_name_placeholder' })}
                  />
                </TextField.Content>
              </TextField.Root>
            )}
          />
        </FormFieldControl>
        <FormFieldError>{errorsTyped?.participantBadge?.[participantIndex]?.name?.message}</FormFieldError>
      </ParticipantFormField>

      <ParticipantFormFieldDate
        $error={
          errorsTyped?.participantBadge?.[participantIndex]?.birthdate
            ? Boolean(errorsTyped?.participantBadge?.[participantIndex]?.birthdate)
            : undefined
        }
      >
        <FormFieldLabel>
          {intl.formatMessage({ id: 'checkout_steps_participants_badge_form_birthdate_text' })}
        </FormFieldLabel>
        <FormFieldControl>
          <TextField.Root error={Boolean(errorsTyped?.participantBadge?.[participantIndex]?.birthdate)}>
            <TextField.Content>
              <TextField.Input
                type="date"
                max={maxBirthdate.toISOString().split('T')[0]}
                min={minBirthdate.toISOString().split('T')[0]}
                {...register(`participantBadge.${participantIndex}.birthdate`, {
                  onChange: (e) => {
                    setValue(`participantBadge.${participantIndex}.birthdate`, e.target.value, {
                      shouldValidate: true,
                    });
                  },
                  validate: {
                    validateAge: (value: unknown) => {
                      if (typeof value !== 'string') {
                        return 'Date not valid';
                      }
                      if (!value) {
                        return true;
                      }
                      const isValidAge = validateAgeBetweenDates(value, minBirthdate, maxBirthdate);

                      return (
                        isValidAge ||
                        intl.formatMessage({ id: 'checkout_steps_participants_badge_form_birthdate_validation_text' })
                      );
                    },
                  },
                })}
              />
            </TextField.Content>
          </TextField.Root>
        </FormFieldControl>
        <FormFieldError>{errorsTyped?.participantBadge?.[participantIndex]?.birthdate?.message}</FormFieldError>
      </ParticipantFormFieldDate>
    </ParticipantFieldsRow>
  );
};
